import { Spacing, Text } from '@vkontakte/vkui';
import image1 from 'img/quota-landing/aiTariff/promoPopup1.png';
import image2 from 'img/quota-landing/aiTariff/promoPopup2.png';
import image3 from 'img/quota-landing/aiTariff/promoPopup3.png';
import React, { type ReactElement, type ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    aiTariffAbVariantB,
    aiTariffAbVariantC1,
    aiTariffAbVariantC2,
    aiTariffIdVarB,
    paySourceAiTariff,
} from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openFastCheckout as openFastCheckoutAction } from 'reactApp/modules/payment/payment.module';
import { ContentSlider } from 'reactApp/sections/QuotaLanding/aiTariffPromoDialog/contentSlider';

import styles from './aiTariffPromoDialog.css';

interface ContentProps {
    title: string;
    description: ReactNode;
    imageUrl: string;
    primaryButtonText: string;
    secondaryButtonText?: string;
}

const description1 = (
    <>
        В Почте нейропомощник соберёт важные письма и перескажет их.
        <br />А ещё напишет письмо с нуля или поправит тон уже написанного.
        <br />
        Например, на деловой
    </>
);

const description2 = (
    <>
        Соберёт дубли фото и подскажет, какие удалить, чтобы освободить
        <br />
        место. Снимки с разными людьми разложит по папкам. Поможет
        <br />
        разобрать фото свайпами, просто смахните их влево или вправо
    </>
);

const name = 'mnz_neiro_tariff_from_info';

const configVariants: Record<'b' | 'c' | 'd', Array<ContentProps>> = {
    b: [
        {
            title: 'Mail Space Нейро — новый тариф с нейросетью',
            description: description1,
            imageUrl: image1,
            // eslint-disable-next-line sonarjs/no-duplicate-string
            primaryButtonText: 'Подключить 256 ГБ с Нейро',
            // eslint-disable-next-line sonarjs/no-duplicate-string
            secondaryButtonText: 'Что умеет в Облаке?',
        },
        {
            title: 'А в Облаке — нейросеть наведёт порядок',
            description: description2,
            imageUrl: image2,
            primaryButtonText: 'Подключить 256 ГБ с Нейро',
            secondaryButtonText: 'Что ещё есть в тарифе?',
        },
        {
            title: 'А ещё в тарифе есть безлимит',
            description: (
                <>
                    Или по‑другому — безлимитная автозагрузка. С ней фото и видео
                    <br />с телефона загрузятся в Облако, но место не займут.
                    <br />А для остальных файлов будут гигабайты
                </>
            ),
            imageUrl: image3,
            primaryButtonText: 'Подключить 256 ГБ с Нейро',
        },
    ],
    c: [
        {
            title: 'Функции нейросети в Основном тарифе. Рассказываем, чем пригодятся',
            description: description1,
            imageUrl: image1,
            // eslint-disable-next-line sonarjs/no-duplicate-string
            primaryButtonText: 'Выбрать тариф',
            secondaryButtonText: 'Что умеет в Облаке?',
        },
        {
            title: 'А в Облаке — нейросеть наведёт порядок',
            description: description2,
            imageUrl: image2,
            primaryButtonText: 'Выбрать тариф',
        },
    ],
    d: [
        {
            title: 'Нейросеть в Основном тарифе. Рассказываем',
            description: description1,
            imageUrl: image1,
            primaryButtonText: 'Выбрать тариф',
            secondaryButtonText: 'Что умеет в Облаке?',
        },
        {
            title: 'А в Облаке — нейросеть наведёт порядок',
            description: description2,
            imageUrl: image2,
            primaryButtonText: 'Выбрать тариф',
        },
    ],
};

const currentVariant = aiTariffAbVariantB
    ? configVariants.b
    : aiTariffAbVariantC1 || aiTariffAbVariantC2
    ? configVariants.c
    : configVariants.d;

export type aiTariffPromoDialogProps = {
    onClose?: () => void;
};

export const aiTariffPromoDialog = memo<aiTariffPromoDialogProps>(({ onClose }) => {
    const dispatch = useDispatch();
    const [index, setIndex] = useState(0);

    const currentContent = currentVariant[index];

    const handleSecondary = useCallback(() => {
        if (index >= currentVariant.length - 1) {
            return;
        }

        sendDwhModal({ name, action: 'click-info' });

        setIndex(index + 1);
    }, [index]);
    const handlePrevious = useCallback(() => {
        if (index === 0) {
            return;
        }

        setIndex(index - 1);
    }, [index]);

    const content = useMemo(
        (): ReactElement => (
            <>
                <div className={styles.sliderWrapper}>
                    <ContentSlider index={index} count={currentVariant.length} onNextClick={handleSecondary} onPrevClick={handlePrevious} />
                </div>
                <Spacing size={16} />
                <Text className={styles.text}>{currentContent.description}</Text>
                <Spacing size={36} />
            </>
        ),
        [index, handlePrevious, handleSecondary, currentContent]
    );

    const handlePrimaryClick = useCallback(() => {
        sendDwhModal({ name, action: 'click' });

        if (aiTariffAbVariantB) {
            dispatch(
                openFastCheckoutAction({
                    paySource: getPaySource(paySourceAiTariff),
                    productId: aiTariffIdVarB.year?.[0],
                    title: 'Подписка Mail Space c нейросетью',
                    onSuccess: () => {},
                    onClose: () => {},
                })
            );
        }

        onClose?.();
    }, [onClose]);

    const handleClose = useCallback(() => {
        sendDwhModal({ name, action: 'close' });

        onClose?.();
    }, [onClose]);

    useEffect(() => {
        sendDwhModal({ name });
    }, []);

    return (
        <WhatsNewDialog
            onClose={handleClose}
            onClick={handlePrimaryClick}
            onSecondary={handleSecondary}
            primaryButtonText={currentContent.primaryButtonText}
            secondaryButtonText={currentContent.secondaryButtonText}
            title={currentContent.title}
            content={content}
            imageUrl={currentContent.imageUrl}
            imageBgColor="var(--vkui--color_background)"
            imageClassName={styles.image}
            imageWrapperClassName={styles.imageWrapper}
            className={styles.root}
            closeIconWrapperClassName={styles.closeWrapper}
            footerClassName={styles.footerClassName}
            buttonTheme="vk"
            dialogSize="large"
            qaId="ai-tariff-promo-dialog"
            bigCloseIcon={false}
            isRebranding
        />
    );
});

aiTariffPromoDialog.displayName = 'aiTariffPromoDialog';
